import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import packageJson from '../../package.json';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const refreshCacheAndReload = () => {
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((names) => {
        for (const name of names) caches.delete(name);
      })
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
};

/**
 * React hook that proactively polls `/meta.json` to detect new deployments
 * and reloads the app when the build version changes.
 *
 * The hook attaches three triggers while it is mounted:
 *   1. `window` `focus`              - covers users returning from another tab.
 *   2. `document` `visibilitychange` - covers users coming back to the tab.
 *   3. A 5-minute interval           - covers idle foreground sessions.
 *
 * The returned `checkVersion` function can also be invoked manually (for
 * example on route changes) so existing call sites continue to work.
 *
 * Overlapping triggers are deduped via a ref so we never run two checks at
 * the same time. Network errors are swallowed so a flaky check does not
 * crash the app - the next trigger will retry naturally.
 *
 * @returns {() => Promise<void>} Manual trigger for the version check.
 */
export default function useVersionCheck() {
  const isCheckingRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    try {
      const meta = await axios.get(`/meta.json?timestamp=${Date.now()}`);

      const responseIsNotJson =
        typeof meta.data === 'string' && meta.data.trim().startsWith('<');
      if (responseIsNotJson) {
        console.warn(
          'Deployment in progress: received HTML instead of meta.json',
        );
        return;
      }

      const latestVersion = meta?.data?.version || '';
      const currentVersion = packageJson.version;
      if (latestVersion && latestVersion !== currentVersion) {
        refreshCacheAndReload();
      }
    } catch (e) {
      // Network errors are non-fatal; the next trigger will retry.
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const onFocus = () => checkVersion();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, [checkVersion]);

  return checkVersion;
}
